<template>
    <v-container fluid class="ma-0 mb-3 pa-0 pl-2 pt-3">
        <v-row class="justify-space-arround">
            <v-col cols="12">
                <v-card class="default" outlined height="100%">
                    <EditorToolbar
                        :headline="model.name"
                        :isModified="isModified"
                        :isWaiting="isWaiting"
                        :editMode="editMode"
                        @onSaveItem="$emit('onSaveItem')"
                        @onDeleteItem="$emit('onDeleteItem')"
                        @onCreateItem="$emit('onCreateItem')"
                        @onFindUsage="$emit('onFindUsage')"
                        :buttons="['new', 'save', 'delete', 'usageAndReplacement', 'translationState']"
                        />
                        <v-container fluid>
                            <v-row no-gutters>
                                <v-col cols="12" :xl="model.tools && model.tools.length ? 8 : 12">
                                    <v-card-text>
                                        <v-text-field
                                            v-model="model.name"
                                            :error-messages="errors.collect('name')"
                                            :label="$t('settings.vendor.name.label')"
                                            :hint="$t('settings.vendor.name.hint')"
                                            persistent-hint
                                            data-vv-name="name"
                                            v-validate="'required'"
                                            spellcheck="true"
                                            autofocus
                                            @input="$emit('modified', $event)"
                                        ></v-text-field>
                                        <v-row>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-text-field
                                                    v-model="streetModel"
                                                    :label="$t('settings.vendor.address.street.label')"
                                                    spellcheck="true"
                                                    @input="$emit('modified', $event)"
                                                ></v-text-field>
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-text-field
                                                    v-model="postcodeModel"
                                                    :label="$t('settings.vendor.address.postcode.label')"
                                                    spellcheck="true"
                                                    @input="$emit('modified', $event)"
                                                ></v-text-field>
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-text-field
                                                    v-model="cityModel"
                                                    :label="$t('settings.vendor.address.city.label')"
                                                    spellcheck="true"
                                                    @input="$emit('modified', $event)"
                                                ></v-text-field>
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="countryModel"
                                                    :label="$t('settings.vendor.address.country.label')"
                                                    :items="countryItems"
                                                    :item-value="function(e) {
                                                        return e.countryCode
                                                    }"
                                                    item-text="countryName"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="6">
                                                <v-autocomplete
                                                    v-model="model.typeOfRecipient"
                                                    :label="$t('settings.vendor.typeOfRecipient.label')"
                                                    :items="externalRecipientCategoryItems"
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6">
                                                <v-autocomplete
                                                    v-model="model.processingType"
                                                    :label="$t('settings.vendor.processingType.label')"
                                                    :items="$t('processingTypeItems')"
                                                    item-text="title"
                                                    item-value="key"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-card-text class="pa-0">
                                        <LeaTranslationTableInput v-model="model.translations" @modified="$emit('modified', $event)" fieldValue="purpose" :fieldText="$t('settings.vendor.purpose.label')" fieldType="textarea" />
                                    </v-card-text>
                                    <v-card-text class="pa-0">
                                        <LeaTranslationTableInput v-model="model.translations" @modified="$emit('modified', $event)" fieldValue="subcontractors" :fieldText="$t('settings.vendor.subcontractors.label')" fieldType="textarea" />
                                    </v-card-text>
                                    <v-card-text>
                                        <v-row>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.appropriateSafeguards"
                                                    :label="$t('settings.vendor.appropriateSafeguards.label')"
                                                    :items="appropriateSafeguardItems"
                                                    multiple
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <div
                                                    class="v-label caption"
                                                    v-text="$t('settings.vendor.dataTransferSecurity.label')"
                                                ></div>
                                                <v-switch
                                                    v-model="model.dataTransferSecurity"
                                                    inset
                                                    dense                                    
                                                    class="v-text-field mt-0"
                                                    @change="$emit('modified', $event)"
                                                >
                                                    <template v-slot:label>
                                                        <div class="pb-5" v-text="model.dataTransferSecurity ? $t('yes') : $t('no')" />
                                                    </template>
                                                </v-switch>
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.groupsOfPersons"
                                                    :label="$t('settings.vendor.groupsOfPersons.label')"
                                                    :items="groupsOfPersonItems"
                                                    multiple
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.legality"
                                                    :label="$t('settings.vendor.legality.label')"
                                                    :items="legalityItems"
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.flexibilityClause"
                                                    :label="$t('settings.vendor.flexibilityClause.label')"
                                                    :items="flexibilityClauseItems"
                                                    multiple
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.nationalDataProtectionLaw"
                                                    :disabled="!countryModel"
                                                    :label="$t('settings.vendor.nationalDataProtectionLaw.label')"
                                                    :items="filteredNationalDataProtectionLawItems"
                                                    multiple
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.personalData"
                                                    :label="$t('settings.vendor.personalData.label')"
                                                    :items="personalDataItems"
                                                    multiple
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.personalDataCategory"
                                                    :label="$t('settings.vendor.personalDataCategory.label')"
                                                    :items="personalDataCategoryItems"
                                                    multiple
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                            <v-col v-if="model.legality === 5" cols="12">
                                                <v-card color="secondary">
                                                    <LegitimateInterest
                                                        form-scope="vendor"
                                                        componentScope="legitimateInterest"
                                                        :i="1"
                                                        :rowId="1"
                                                        v-model="legitimateInterestModel"
                                                        :currentUiLanguage="this.currentLanguage"
                                                        :ref="'vendor_legitimate_interest_1'"
                                                        fieldName="vendor"
                                                        translatable
                                                        :availableLanguages="availableLanguages"
                                                        @modified="$emit('modified', $event)"
                                                    />
                                                </v-card>
                                            </v-col>
                                            <v-col v-if="model.legality === 1" cols="12" md="6" lg="4" xl="3">
                                                <v-autocomplete
                                                    v-model="model.nationalLegalObligation"
                                                    :label="$t('settings.vendor.nationalLegalObligation.label')"
                                                    :items="recipientNationalLegalObligationItems"
                                                    item-text="title"
                                                    item-value="id"
                                                    clearable
                                                    @input="$emit('modified', $event)"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-card color="secondary" class="mt-3">
                                            <Upload
                                                v-model="model.dpaEnclosure"
                                                :itemText="'fileName'"
                                                :itemKey="'clientId'"
                                                :id="1"
                                                :enclosureType="'dpa'"
                                                :label="$t('settings.vendor.dpaEnclosure.label')"
                                                :hint="$t('settings.vendor.dpaEnclosure.hint')"
                                                @modified="$emit('modified', $event)"
                                            />
                                        </v-card>
                                    </v-card-text>
                                    <v-card-text class="pa-0">
                                        <LeaTranslationTableInput v-model="model.translations" @modified="$emit('modified', $event)" fieldValue="lcm" :fieldText="$t('settings.vendor.lcm.label')" :hint="$t('settings.vendor.lcm.hint')" fieldType="text" />
                                    </v-card-text>
                                </v-col>
                                <v-col v-if="model.tools && model.tools.length" cols="12" xl="4">
                                    <v-card-title class="overline primary--text">{{ $t('settings.vendor.mappedTools') }}</v-card-title>
                                    <v-card-text style="max-height: 50vh; overflow-y: auto">
                                        <v-list dense>
                                            <template v-for="item in model.tools">
                                                    <v-list-item :key="item.id" target="_blank" :to="{ name: 'SettingsTools', params: { action: 'list', id: item.id } }">
                                                        <v-list-item-action>
                                                            <v-icon small color="primary">mdi-open-in-new</v-icon>
                                                        </v-list-item-action>
                                                        <v-list-item-content>
                                                            <v-list-item-title v-text="item.title" />
                                                        </v-list-item-content>
                                                    </v-list-item>
                                            </template>
                                        </v-list>
                                    </v-card-text>
                                </v-col>
                            </v-row>
                        </v-container>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { isUUID } from '@/components/vvt/ModelMixin.js';
import Upload from '@/components/Upload.vue';
import LegitimateInterest from '@/components/LegitimateInterest';
import EditorToolbar from '../EditorToolbar.vue';
import LeaTranslationTableInput from '@/components/Input/LeaTranslationTableInput';

export default {
    name: 'SettingsVendorEditorMask',
    inject: {
        $validator: '$validator',
    },
    components: {
        Upload,
        LegitimateInterest,
        EditorToolbar,
        LeaTranslationTableInput
    },
    props: {
        value: {
            type: Object,
            default: null
        },
        availableLanguages: {
            type: Array,
            default: () => []
        },
        currentLanguage: {
            type: String,
            default: null
        },
        isWaiting: {
            type: Boolean,
            default: false
        },
        isModified: {
            type: Boolean,
            default: false
        },
        countryItems: {
            type: Array,
            default: () => []
        },
        externalRecipientCategoryItems: {
            type: Array,
            default: () => []
        },
        appropriateSafeguardItems: {
            type: Array,
            default: () => []
        },
        groupsOfPersonItems: {
            type: Array,
            default: () => []
        },
        legalityItems: {
            type: Array,
            default: () => []
        },
        flexibilityClauseItems: {
            type: Array,
            default: () => []
        },
        recipientNationalLegalObligationItems: {
            type: Array,
            default: () => []
        },
        nationalDataProtectionLawItems: {
            type: Array,
            default: () => []
        },
        personalDataCategoryItems: {
            type: Array,
            default: () => []
        },
        personalDataItems: {
            type: Array,
            default: () => []
        }
    },
    i18n: {
        messages: {
            en: require('@/locales/Settings/en.json'),
            de: require('@/locales/Settings/de.json'),
        },
    },
    data() {
        return {
            model: this.value
        };
    },
    mounted() {
        if(!this.model.address) {
            this.$set(this.model, 'address', this.defaultAddress);
        }
        if(!this.model.legitimateInterest || !this.$objectPropertyExists(this.model.legitimateInterest, 'id')) {
            this.$set(this.model, 'legitimateInterest', this.defaultLegitimateInterest);
        }
    },
    computed: {
        editMode() {
            return !isUUID(this.model.id);
        },
        filteredNationalDataProtectionLawItems() {  
            if(!this.countryModel) {
                return [];
            }
            
            return this.nationalDataProtectionLawItems.filter(
                x => !x.country || x.country === this.countryModel.toUpperCase()
            );
        },
        defaultAddress () {
            return {
                street: null,
                postcode: null,
                city: null,
                country: null
            }
        },
        defaultLegitimateInterest () {
            let li = {
                id: null,
                clientId: null,
                originalId: null,
                category: null,
                title: null,
                locale: null,
                translations: []
            }
            let translations = [];
            this.availableLanguages.forEach(locale => {
                translations.push(
                    {
                        interestResponsiblePerson: null,
                        interestPersonConcerned: null,
                        necessity: null,
                        balancingOfInterests: null,
                        locale
                    }
                );
            });

            translations.sort((a, b) => {
                return (b.locale === this.currentLanguage && a.locale !== this.currentLanguage) ? 1 : -1;
            });

            li.translations = translations;

            return li;
        },
        streetModel: {
            get() {
                return this.model.address && this.model.address.street ? this.model.address.street : null;
            },
            set(val) {
                if(!this.$objectPropertyExists(this.model, 'address') || !this.model.address) {
                    this.$set(this.model, 'address', {});
                }
                this.$set(this.model.address, 'street', val);
            },
        },
        postcodeModel: {
            get() {
                return this.model.address && this.model.address.postcode ? this.model.address.postcode : null;
            },
            set(val) {
                if(!this.$objectPropertyExists(this.model, 'address') || !this.model.address) {
                    this.$set(this.model, 'address', {});
                }
                this.$set(this.model.address, 'postcode', val);
            },
        },
        cityModel: {
            get() {
                return this.model.address && this.model.address.city ? this.model.address.city : null;
            },
            set(val) {
                if(!this.$objectPropertyExists(this.model, 'address') || !this.model.address) {
                    this.$set(this.model, 'address', {});
                }
                this.$set(this.model.address, 'city', val);
            },
        },
        countryModel: {
            get() {
                return this.model.address && this.model.address.country ? this.model.address.country : null;
            },
            set(val) {
                if(!this.$objectPropertyExists(this.model, 'address') || !this.model.address) {
                    this.$set(this.model, 'address', {});
                }
                this.$set(this.model.address, 'country', val);
            },
        },
        legitimateInterestModel: {
            get() {
                return this.model.legitimateInterest;
            },
            set(val) {
                this.$set(this.model, 'legitimateInterest', val);
            },
        },
    },
};
</script>
